/**
 * Member query hooks
 *
 * Provides React Query hooks for fetching member data.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, handleSupabaseError, handleUnexpectedError, fetchAllRows } from '@/lib';
import type { Member, MemberDirectoryEntry } from '@/types';
import { MEMBER_QUERY_KEYS } from '@/constants';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Hook to fetch all members
 *
 * @param includeDeleted - Whether to include soft-deleted members (default: false)
 * @returns Query result with array of members
 */
export function useMembers(includeDeleted = false) {
  return useQuery({
    queryKey: [...MEMBER_QUERY_KEYS.ALL, includeDeleted],
    queryFn: async () => {
      try {
        // Paged (fetchAllRows) so the member directory never silently
        // truncates at PostgREST's 1,000-row cap as the roster grows.
        const { data, error } = await fetchAllRows<Member>(() => {
          let query = supabase
            .from('members')
            .select('*')
            .order('created_at', { ascending: false });

          // By default, exclude soft deleted members
          if (!includeDeleted) {
            query = query.filter('deleted_at', 'is', null);
          }

          return query;
        });

        if (error) handleSupabaseError(error, { operation: 'fetching members' });

        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useMembers', operation: 'loading members' });
      }
    },
  });
}

/** Columns behind MemberDirectoryEntry — keep the two in step. */
export const MEMBER_DIRECTORY_COLUMNS =
  'id, first_name, last_name, email, whatsapp_number, date_of_birth, gender';

/**
 * How long the directory is served from cache. Member edits invalidate it
 * straight away (it sits under MEMBER_QUERY_KEYS.ALL); this only bounds how
 * late a signup made elsewhere — the mobile app, another admin — shows up.
 */
export const MEMBER_DIRECTORY_STALE_TIME_MS = 15 * 60 * 1000;

/**
 * The slim, non-deleted roster for member pickers and the header search:
 * seven columns instead of every column of every member, shared by every
 * caller and kept for 15 minutes. Use `useMembers` only where a screen really
 * shows the full record.
 */
export function useMemberDirectory() {
  return useQuery({
    queryKey: MEMBER_QUERY_KEYS.DIRECTORY,
    queryFn: async () => {
      try {
        const { data, error } = await fetchAllRows<MemberDirectoryEntry>(() =>
          supabase
            .from('members')
            .select(MEMBER_DIRECTORY_COLUMNS)
            .is('deleted_at', null)
            .order('created_at', { ascending: false }),
        );

        if (error) handleSupabaseError(error, { operation: 'fetching the member directory' });

        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useMemberDirectory', operation: 'loading the member directory' });
      }
    },
    staleTime: MEMBER_DIRECTORY_STALE_TIME_MS,
    gcTime: MEMBER_DIRECTORY_STALE_TIME_MS * 2,
  });
}

/** A search miss refetches the directory only if its data is at least this old. */
export const MEMBER_DIRECTORY_MISS_REFETCH_MIN_AGE_MS = 30_000;

/**
 * Refetch the member directory once when a search finds nobody.
 *
 * The directory is cached for 15 minutes and skipped by the tab-focus refresh,
 * so a member who signed up on the mobile app a minute ago — the usual
 * front-desk onboarding — isn't in it yet. Rather than refetch ~3,000 rows on
 * a timer, refetch at the one moment it matters: someone typed a real query
 * (3+ characters, settled for 400 ms) and got zero matches.
 */
export function useRefreshMemberDirectoryOnMiss(query: string, matchCount: number) {
  const queryClient = useQueryClient();
  const liveQuery = query.trim();
  const settledQuery = useDebouncedValue(liveQuery, 400);

  useEffect(() => {
    // `matchCount` describes the live query; only trust it once the input has
    // stopped changing, or a count for "anab" gets paired with "ana".
    if (settledQuery !== liveQuery) return;
    if (settledQuery.length < 3 || matchCount > 0) return;
    const state = queryClient.getQueryState(MEMBER_QUERY_KEYS.DIRECTORY);
    if (!state || state.fetchStatus === 'fetching') return;
    if (Date.now() - state.dataUpdatedAt < MEMBER_DIRECTORY_MISS_REFETCH_MIN_AGE_MS) return;
    void queryClient.refetchQueries({ queryKey: MEMBER_QUERY_KEYS.DIRECTORY, exact: true });
  }, [settledQuery, liveQuery, matchCount, queryClient]);
}

/**
 * Hook to fetch a single member by ID
 *
 * @param id - The member's UUID
 * @returns Query result with member data
 */
export function useMember(id: string) {
  return useQuery({
    queryKey: MEMBER_QUERY_KEYS.BY_ID(id),
    queryFn: async () => {
      if (!id) {
        throw new Error('Member ID is required');
      }

      try {
        const { data, error } = await supabase
          .from('members')
          .select('*')
          .eq('id', id)
          .is('deleted_at', null)  // Exclude soft-deleted members
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            throw new Error('Member not found or has been deleted');
          }
          handleSupabaseError(error, { operation: 'fetching member', params: { memberId: id } });
        }

        if (!data) {
          throw new Error('Member not found');
        }

        return data as Member;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useMember', operation: 'loading member details', params: { memberId: id } });
      }
    },
    enabled: !!id, // Disable query when ID is empty
  });
}

/**
 * Hook for fetching deleted members (admin only)
 *
 * @returns Query result with array of soft-deleted members
 */
export function useDeletedMembers() {
  return useQuery({
    queryKey: MEMBER_QUERY_KEYS.DELETED,
    queryFn: async () => {
      try {
        // Paged (fetchAllRows) so the list never silently truncates at
        // PostgREST's 1,000-row cap.
        const { data, error } = await fetchAllRows<Member>(() =>
          supabase
            .from('members')
            .select('*')
            .not('deleted_at', 'is', null)
            .order('deleted_at', { ascending: false }),
        );

        if (error) handleSupabaseError(error, { operation: 'fetching deleted members' });

        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useDeletedMembers', operation: 'loading deleted members' });
      }
    },
  });
}
